import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Heart, HeartHandshake } from "lucide-react";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * ConditionDetailsDialog — the "Autism (ASD)", "ADHD", … condition
 * explainer popup rendered when a parent taps a card or chip in the
 * "Who We Support" section on the landing page.
 *
 * Visual style intentionally mirrors ``SampleReportDialog`` (same
 * shadcn Dialog + gradient header + slate-50/40 body + teal CTA card at
 * the bottom) — the design-review ask was for parity with the sample
 * report popup already used on ``/free-screening``.
 *
 * Props:
 *   open           : boolean         — controlled visibility
 *   onOpenChange   : (bool) => void  — controlled open/close
 *   condition      : ConditionDetail — one of the entries from
 *                                     ``conditionDetails.js``. Passing
 *                                     null/undefined renders nothing so
 *                                     the parent can lazy-select.
 */
export default function ConditionDetailsDialog({ open, onOpenChange, condition }) {
  if (!condition) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="condition-details-dialog"
        className="p-0 overflow-hidden border-0 rounded-3xl max-w-2xl w-[calc(100vw-1.25rem)] sm:w-full max-h-[88vh] flex flex-col"
      >
        {/* Header — matches SampleReportDialog's gradient card look */}
        <div className="bg-gradient-to-br from-[#F0FDFA] to-white px-5 sm:px-8 pt-6 pb-5 border-b border-slate-100">
          <DialogHeader className="text-left">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-bold text-[#2F6F6A]">
                <Heart className="h-3 w-3 fill-current" strokeWidth={0} /> Who We Support
              </span>
            </div>
            <DialogTitle
              data-testid="condition-details-title"
              className="font-display text-xl sm:text-2xl font-extrabold text-[#1F2933]"
            >
              {condition.title}
            </DialogTitle>
            <DialogDescription className="text-[13px] sm:text-sm text-slate-600 mt-1">
              Tailored guidance and activities delivered by Kiddo+.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Body — two content cards + CTA. The whatIs / how sections are
            surfaced as separate cards so the visual rhythm matches the
            "Main Concern / Goals" pair in the sample-report popup. */}
        <div className="px-5 sm:px-8 py-5 overflow-y-auto space-y-4 bg-slate-50/40">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="rounded-2xl p-4 sm:p-5 bg-white border border-slate-100 shadow-sm"
          >
            <p className="text-[11px] uppercase tracking-wider font-bold text-[#2F6F6A]">
              {condition.whatIsHeading}
            </p>
            <p
              data-testid="condition-details-what-is"
              className="text-[13px] sm:text-sm text-slate-700 leading-relaxed mt-2 whitespace-pre-line"
            >
              {condition.whatIsBody}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1 }}
            className="rounded-2xl p-4 sm:p-5 bg-white border border-slate-100 shadow-sm"
          >
            <div className="flex items-center gap-2">
              <HeartHandshake className="h-4 w-4 text-[#14B8A6]" />
              <p className="text-[11px] uppercase tracking-wider font-bold text-[#2F6F6A]">
                {condition.howHeading}
              </p>
            </div>
            <p
              data-testid="condition-details-how"
              className="text-[13px] sm:text-sm text-slate-700 leading-relaxed mt-2 whitespace-pre-line"
            >
              {condition.howBody}
            </p>
          </motion.div>

          {/* CTA card — matches sample-report bottom card exactly. */}
          <div className="rounded-2xl bg-[#2F6F6A] text-white p-5 sm:p-6 text-center">
            <p className="font-display font-extrabold text-lg sm:text-xl">
              See how Kiddo+ can help your child
            </p>
            <p className="text-[13px] text-white/85 mt-1">
              Personalised activities · Progress tracking · No card required
            </p>
            <Link
              to="/signup"
              onClick={() => onOpenChange?.(false)}
              className="mt-4 inline-flex items-center gap-2 bg-[#F4C430] hover:bg-[#fbd34c] text-[#1F2933] rounded-full px-6 py-3 font-extrabold text-sm sm:text-base shadow"
              data-testid="condition-details-cta"
            >
              <Sparkles className="h-4 w-4" /> Start Free Today <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
